import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { SessionUser } from '@/types/api';

export const SESSION_KEY = ['session'] as const;

export function useSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: ({ signal }) => api<{ user: SessionUser | null }>('/auth/me', { signal, withLang: false }).then((r) => r.user),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Clear every cached response that may contain viewer-specific prices. */
function resetViewerData(qc: ReturnType<typeof useQueryClient>) {
  qc.removeQueries({ queryKey: ['products'] });
  qc.removeQueries({ queryKey: ['product'] });
  qc.removeQueries({ queryKey: ['admin'] });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) => api<{ user: SessionUser }>('/auth/login', { method: 'POST', body: input }).then((r) => r.user),
    onSuccess: (user) => {
      resetViewerData(qc);
      qc.setQueryData(SESSION_KEY, user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      resetViewerData(qc);
      qc.setQueryData(SESSION_KEY, null);
    },
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) => api<void>('/auth/change-password', { method: 'POST', body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SESSION_KEY }),
  });
}
